import * as React from 'react';
import { Component, RefObject } from 'react';
import { ButtonCallToAction, ButtonTertiary, MeatballMenu, PromptConfirmationModal } from 'ui';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { CodetableTranslation } from '../../constants';
import { DisabledTag } from 'app/area/codetable/common/disabled-tag/disabled-tag';
import { CodeActionType, ReadOnlyConfiguration, ReadOnlyType } from 'app/area/codetable/item/types';
import { CodeItem, DeleteCodeItemPayload } from 'app/area/codetable/item/types-store';
import { canMergeCode } from 'app/area/codetable/common/merge-code/can-merge';
import { replaceTemplateString } from 'app/infrastructure/platform';
import styles from './codetable-sticky-header.scss';

export interface CodetableStickyHeaderProps {
  codetableId: number;
  codeItem: CodeItem;
  breadcrumbs: number[];
  codeId: number;
  codeDescription: string;
  originalCodeDescription: string;
  codetableName: string;
  actionType: CodeActionType;
  translation: CodetableTranslation;
  currentLongDescription?: string;
  originalLongDescription?: string;
  disabled: Date;
  readOnlyConfig: ReadOnlyConfiguration;
  canDelete: boolean;
  canMerge: boolean;
  canDuplicateHierarchy: boolean;
  shouldPrompt: boolean;
  save: (withDescendants: boolean) => void;
  cancel: () => void;
  deleteCodeItem: (payload: DeleteCodeItemPayload) => void;
  duplicateCodeItem: () => void;
  mergeCodeItem: () => void;
  toggleDuplicateHierarchy: () => void;
}

export class CodetableStickyHeader extends Component<CodetableStickyHeaderProps> {

  private longDescriptionRef: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  private deleteCodeItem = () => {
    const { deleteCodeItem, codeId, breadcrumbs, translation } = this.props;

    deleteCodeItem({
      breadcrumbs, codeId, successMessage: translation.code_item_successfully_deleted,
      violationErrorMessage: translation.merge_code_violation_error
    });
  };

  public render() {
    const {
      shouldPrompt, codetableId, codeDescription, originalCodeDescription, codetableName,
      actionType, codeId, codeItem, readOnlyConfig, canDelete, canDuplicateHierarchy,
      currentLongDescription, originalLongDescription, translation,
      save, cancel, duplicateCodeItem, deleteCodeItem, mergeCodeItem
    } = this.props;

    const buttonText = {
      [CodeActionType.Add]: translation.add_code,
      [CodeActionType.Edit]: translation.update_code,
      [CodeActionType.Duplicate]: translation.update_code
    }[actionType];

    const longDescription = actionType === CodeActionType.Duplicate ? originalLongDescription : currentLongDescription;
    const canMerge = canMergeCode(codetableId, this.props.canMerge, this.props.codeItem?.category);
    const onSave = canDuplicateHierarchy ? this.props.toggleDuplicateHierarchy : () => save(false);
    const disabled = !shouldPrompt && actionType === CodeActionType.Edit;

    return (
      <StickyHeader className={styles.stickyHeader} rightSideContent={<>
        <StickyHeaderButtonContainer>
          <ButtonTertiary id={`codetable-sticky-header-cancel-button-${codetableId}`} onClick={cancel}>
            {translation.cancel}
          </ButtonTertiary>
          {!readOnlyConfig[ReadOnlyType.save]
            && <ButtonCallToAction
              id={`codetable-sticky-header-save-button-${codetableId}`}
              onClick={onSave}
              disabled={disabled}>
              {buttonText}
            </ButtonCallToAction>}
        </StickyHeaderButtonContainer>
        {!!codeId && codeId > 0 && actionType !== CodeActionType.Duplicate && (duplicateCodeItem || canDelete)
          && <MeatballMenu id="codetable-sticky-header-meatballmenu">
            {duplicateCodeItem && <li>
              <button type="button" className="dropdown-item" onClick={duplicateCodeItem}>
                {translation?.duplicate_code}
              </button>
            </li>}

            {canDelete && !canMerge && deleteCodeItem && <li>
              <PromptConfirmationModal
                title={translation.delete_code_item_title}
                description={replaceTemplateString(translation.delete_confirm, codeItem.name)}
                continueText={translation.delete}
                cancelText={translation.cancel}
                onContinue={this.deleteCodeItem} >
                <button type="button" className="dropdown-item">
                  {translation?.delete}
                </button>
              </PromptConfirmationModal>
            </li>}

            {canDelete && canMerge && mergeCodeItem && <li>
              <button type="button" className="dropdown-item" onClick={mergeCodeItem}>
                {translation?.delete}
              </button>
            </li>}
          </MeatballMenu>}
      </>}>
        <div className={styles.title}>{codetableName}</div>
        <div className={styles.actionTitleContainer}>
          {actionType === CodeActionType.Edit && <span className={styles.id}>#{codeId}</span>}
          <h2 className={styles.actionTitle}>
            {actionType === CodeActionType.Edit ? codeDescription : translation.new_code_item}
          </h2>
          <div><DisabledTag codeItem={codeItem} translation={translation} /></div>
        </div>
        {actionType === CodeActionType.Duplicate && codeId && originalCodeDescription
          && <div className={`${styles.longDescription} mt-2`}>
            {replaceTemplateString(translation.duplicate_hierarchy_copied_from, codeId)} <b>{originalCodeDescription}</b>
          </div>}
        {actionType !== CodeActionType.Add && !!longDescription
          ? <div className={`${styles.longDescriptionContainer} ${!!longDescription ? '' : styles.hiddenLongDescriptionContainer}`}>
            <div id="codetable-sticky-header-long-description" className={styles.longDescription} ref={this.longDescriptionRef}>
              {longDescription}
            </div>
          </div>
          : null}
      </StickyHeader>
    );
  }
}
